import { resolveFieldDataPath } from "../constants/fieldPathRegistry";
import {
  IPolicyConditionField,
  IPolicyFormField,
  IPolicyLayoutSection,
  IPolicyRuleCategory,
  IPolicySubSection,
} from "../types/builder";
import {
  PolicyConditionField,
  PolicyConditionLinesNode,
  PolicyFieldDependency,
  PolicyFieldNode,
  PolicyFormNode,
  PolicyFieldOptionSource,
  PolicyRuleCategoryNode,
  PolicyRuleDefinitionNode,
  PolicyRuleGroupTemplate,
  PolicySectionNode,
  PolicyTemplatedGroupsNode,
} from "../types/policyForm";
import { isPolicyRulesLayoutSection } from "../utils";
import { mapInputTypeToApiValueType } from "../utils/valueTypeMapper";
import { buildExclusiveChoosers } from "../utils/exclusiveConditionChoosers";
import {
  getRuleGroupSubSectionsForCategory,
  getOperatorsBySymbols,
  getValidationsByKeys,
  PolicyBuilderIndexes,
} from "./policyBuilderIndexes";

const LOGICAL_OPERATOR_SYMBOLS = ["AND", "OR"];

function buildLogicalOperatorsOptionSource(
  indexes: PolicyBuilderIndexes,
): PolicyFieldOptionSource {
  const staticOptions = LOGICAL_OPERATOR_SYMBOLS.flatMap((symbol) => {
    const operator = indexes.operatorsBySymbol[symbol];
    if (!operator) {
      return [];
    }

    return [
      {
        label: operator.label,
        value: operator.symbol,
      },
    ];
  });

  return {
    key: "LOGICAL_OPERATORS",
    staticOptions,
    api: null,
    supportsSearch: false,
    searchParam: null,
    defaultParams: null,
    labelField: "label",
    valueField: "value",
  };
}

function mapOptionSource(
  indexes: PolicyBuilderIndexes,
  optionSourceKey: string | null,
): PolicyFieldOptionSource | null {
  if (!optionSourceKey) {
    return null;
  }

  if (optionSourceKey === "LOGICAL_OPERATORS") {
    return buildLogicalOperatorsOptionSource(indexes);
  }

  const source = indexes.optionSourcesByKey[optionSourceKey];
  if (!source) {
    return null;
  }

  return {
    key: source.key,
    staticOptions: source.options ?? [],
    api: source.api,
    supportsSearch: source.supports_search,
    searchParam: source.search_param,
    defaultParams: source.default_params,
    labelField: source.label_field ?? "label",
    valueField: source.value_field ?? "value",
  };
}

function mapFieldDependency(
  indexes: PolicyBuilderIndexes,
  dependsOnKey: string | null,
  parentDataPath: string,
): PolicyFieldDependency | null {
  if (!dependsOnKey) {
    return null;
  }

  const dependsOnField = indexes.fieldsByKey[dependsOnKey];
  if (!dependsOnField) {
    return {
      fieldKey: dependsOnKey,
      dependsOnFieldKey: dependsOnKey,
      dependsOnDataPath: dependsOnKey,
    };
  }

  return {
    fieldKey: dependsOnKey,
    dependsOnFieldKey: dependsOnField.key,
    dependsOnDataPath: resolveFieldDataPath(
      dependsOnField.key,
      dependsOnField.field,
      parentDataPath,
    ),
  };
}

export function mapConditionFieldToView(
  conditionField: IPolicyConditionField,
  indexes: PolicyBuilderIndexes,
  templateKey?: string,
): PolicyConditionField {
  return {
    key: conditionField.key,
    dataPath: conditionField.field,
    label: conditionField.label,
    inputType: conditionField.input_type,
    apiValueType: mapInputTypeToApiValueType(conditionField.input_type),
    allowedOperators: getOperatorsBySymbols(
      indexes,
      conditionField.allowed_operators,
    ),
    defaultOperator:
      indexes.operatorsBySymbol[conditionField.default_operator] ?? null,
    optionSource: mapOptionSource(indexes, conditionField.option_source_key),
    placeholder: null,
    unit: conditionField.unit,
    displayTemplate: conditionField.display_template,
    validationKeys: conditionField.validation_keys ?? [],
    validations: getValidationsByKeys(indexes, conditionField.validation_keys),
    required: Boolean(conditionField.required),
    autoRender: conditionField.auto_render,
    substituteKeys: conditionField.substitute_keys ?? [],
    templateKey: templateKey || undefined,
  };
}

function getConditionFieldsByKeys(
  fieldKeys: string[] | null,
  indexes: PolicyBuilderIndexes,
  templateKey?: string,
): PolicyConditionField[] {
  if (!fieldKeys?.length) {
    return [];
  }

  return fieldKeys
    .map((fieldKey) => indexes.conditionFieldsByKey[fieldKey])
    .filter((field): field is IPolicyConditionField => Boolean(field))
    .map((field) => mapConditionFieldToView(field, indexes, templateKey));
}

export function mapFieldToNode(
  field: IPolicyFormField,
  indexes: PolicyBuilderIndexes,
  parentDataPath: string,
): PolicyFieldNode {
  const dataPath = resolveFieldDataPath(field.key, field.field, parentDataPath);
  const validationKeys = field.validation_keys ?? [];
  const fieldNode: PolicyFieldNode = {
    kind: "field",
    key: field.key,
    dataPath,
    label: field.label,
    inputType: field.input_type,
    description: field.description,
    placeholder: field.placeholder,
    helpText: field.help_text,
    required: field.required,
    multiple: field.multiple,
    readonly: field.readonly,
    defaultValue: field.default_value,
    validationKeys,
    validations: getValidationsByKeys(indexes, validationKeys),
    optionSource: mapOptionSource(indexes, field.option_source_key),
    dependsOn: mapFieldDependency(indexes, field.depends_on, parentDataPath),
    derivedSource: field.source,
  };

  if (field.key === "CONDITION_VALUE_TYPE") {
    fieldNode.readonly = true;
  }

  return fieldNode;
}

function mapFieldKeysToNodes(
  fieldKeys: string[] | null,
  indexes: PolicyBuilderIndexes,
  parentDataPath: string,
): PolicyFieldNode[] {
  if (!fieldKeys?.length) {
    return [];
  }

  return fieldKeys
    .map((fieldKey) => indexes.fieldsByKey[fieldKey])
    .filter((field): field is IPolicyFormField => Boolean(field))
    .map((field) => mapFieldToNode(field, indexes, parentDataPath));
}

function mapRuleGroupTemplate(
  subSection: IPolicySubSection,
  indexes: PolicyBuilderIndexes,
): PolicyRuleGroupTemplate {
  const defaultConditions = getConditionFieldsByKeys(
    subSection.field_keys,
    indexes,
    subSection.key,
  );
  const optionalConditions = getConditionFieldsByKeys(
    subSection.condition_field_keys,
    indexes,
    subSection.key,
  );

  return {
    key: subSection.key,
    label: subSection.label,
    description: subSection.description,
    conditionLines: mapConditionLinesNode(indexes, {
      defaultConditions,
      optionalConditions,
    }),
  };
}

function mapConditionLinesNode(
  indexes: PolicyBuilderIndexes,
  conditions: {
    defaultConditions: PolicyConditionField[];
    optionalConditions: PolicyConditionField[];
  },
): PolicyConditionLinesNode {
  const conditionLinesSubSection = indexes.subSectionsByKey.CONDITION_LINES;
  const validationKeys = conditionLinesSubSection?.validation_keys ?? [];

  return {
    kind: "conditionLines",
    key: conditionLinesSubSection?.key ?? "CONDITION_LINES",
    label: conditionLinesSubSection?.label ?? "Condition Lines",
    description: conditionLinesSubSection?.description ?? "",
    dataPath: "condition_lines",
    defaultConditions: conditions.defaultConditions,
    optionalConditions: conditions.optionalConditions,
    exclusiveChoosers: buildExclusiveChoosers(conditions.defaultConditions),
    itemFields: mapFieldKeysToNodes(
      conditionLinesSubSection?.field_keys ?? null,
      indexes,
      "",
    ),
    validationKeys,
    validations: getValidationsByKeys(indexes, validationKeys),
  };
}

function mapTemplatedGroupsNode(
  indexes: PolicyBuilderIndexes,
  category: IPolicyRuleCategory,
): PolicyTemplatedGroupsNode {
  const ruleGroupsSubSection = indexes.subSectionsByKey.RULE_GROUPS;
  const subSections = getRuleGroupSubSectionsForCategory(category, indexes);

  const defaultFieldKeys = subSections.flatMap(
    (subSection) => subSection.field_keys ?? [],
  );
  const optionalFieldKeys = subSections.flatMap(
    (subSection) => subSection.condition_field_keys ?? [],
  );

  const conditionLineTemplatesMap = new Map<string, string>();
  subSections.forEach((subSection) => {
    (subSection.field_keys ?? []).forEach((key) => {
      if (!conditionLineTemplatesMap.has(key)) {
        conditionLineTemplatesMap.set(key, subSection.key);
      }
    });
    (subSection.condition_field_keys ?? []).forEach((key) => {
      if (!conditionLineTemplatesMap.has(key)) {
        conditionLineTemplatesMap.set(key, subSection.key);
      }
    });
  });

  const defaultConditions = [...new Set(defaultFieldKeys)]
    .map((fieldKey) => indexes.conditionFieldsByKey[fieldKey])
    .filter((field): field is IPolicyConditionField => Boolean(field))
    .map((field) =>
      mapConditionFieldToView(
        field,
        indexes,
        conditionLineTemplatesMap.get(field.key),
      ),
    );

  const optionalConditions = [...new Set(optionalFieldKeys)]
    .map((fieldKey) => indexes.conditionFieldsByKey[fieldKey])
    .filter((field): field is IPolicyConditionField => Boolean(field))
    .map((field) =>
      mapConditionFieldToView(
        field,
        indexes,
        conditionLineTemplatesMap.get(field.key),
      ),
    );

  const conditionLines = mapConditionLinesNode(indexes, {
    defaultConditions,
    optionalConditions,
  });

  const allowedTemplates = subSections.map((subSection) =>
    mapRuleGroupTemplate(subSection, indexes),
  );

  const validationKeys = ruleGroupsSubSection?.validation_keys ?? [];
  const operatorField = mapFieldKeysToNodes(
    ruleGroupsSubSection?.field_keys ?? ["GROUP_OPERATOR"],
    indexes,
    "rule_definition.groups",
  ).find((field) => field.key === "GROUP_OPERATOR");

  return {
    kind: "templatedGroups",
    key: ruleGroupsSubSection?.key ?? "RULE_GROUPS",
    label: ruleGroupsSubSection?.label ?? "Groups",
    description: ruleGroupsSubSection?.description ?? "",
    dataPath: "rule_definition.groups",
    operatorField,
    validationKeys,
    validations: getValidationsByKeys(indexes, validationKeys),
    conditionLines,
    allowedTemplates,
  };
}

function mapRuleDefinitionNode(
  indexes: PolicyBuilderIndexes,
  category: IPolicyRuleCategory,
): PolicyRuleDefinitionNode {
  const ruleDefinitionSubSection = indexes.subSectionsByKey.RULE_DEFINITION;
  const operatorField = mapFieldKeysToNodes(
    ruleDefinitionSubSection?.field_keys ?? ["RULE_DEFINITION_OPERATOR"],
    indexes,
    "",
  ).find((field) => field.key === "RULE_DEFINITION_OPERATOR");

  if (!operatorField) {
    throw new Error(
      "RULE_DEFINITION_OPERATOR field is missing from builder config",
    );
  }

  return {
    kind: "ruleDefinition",
    key: ruleDefinitionSubSection?.key ?? "RULE_DEFINITION",
    label: ruleDefinitionSubSection?.label ?? "Rule Definition",
    description: ruleDefinitionSubSection?.description ?? "",
    operatorField,
    groups: mapTemplatedGroupsNode(indexes, category),
  };
}

function adaptBaseFieldForCategory(
  field: PolicyFieldNode,
  category: IPolicyRuleCategory,
): PolicyFieldNode {
  if (field.key !== "RULE_CATEGORY") {
    return field;
  }

  return {
    ...field,
    defaultValue: category.key,
    readonly: true,
  };
}

function mapRuleCategoryNode(
  category: IPolicyRuleCategory,
  indexes: PolicyBuilderIndexes,
): PolicyRuleCategoryNode {
  const ruleCardSubSection = indexes.subSectionsByKey.RULE_CARD;
  const validationKeys = ruleCardSubSection?.validation_keys ?? [];

  return {
    kind: "ruleCategory",
    key: category.key,
    label: category.label,
    description: category.description,
    ruleCategoryKey: category.key,
    validationKeys,
    validations: getValidationsByKeys(indexes, validationKeys),
    baseFields: mapFieldKeysToNodes(
      ruleCardSubSection?.field_keys ?? null,
      indexes,
      "",
    ).map((field) => adaptBaseFieldForCategory(field, category)),
    ruleDefinition: mapRuleDefinitionNode(indexes, category),
  };
}

export function mapRuleCategoryNodes(
  indexes: PolicyBuilderIndexes,
): PolicyRuleCategoryNode[] {
  return indexes.config.rule_categories.map((category) =>
    mapRuleCategoryNode(category, indexes),
  );
}

function mapLayoutSubSectionToNode(
  subSectionKey: string,
  indexes: PolicyBuilderIndexes,
): PolicySectionNode | null {
  const subSection = indexes.subSectionsByKey[subSectionKey];
  if (!subSection) {
    return null;
  }

  const validationKeys = subSection.validation_keys ?? [];
  const children: PolicyFormNode[] = [
    ...mapFieldKeysToNodes(subSection.field_keys, indexes, ""),
    ...mapLayoutSubSectionKeys(subSection.sub_section_keys, indexes),
  ];

  if (children.length === 0) {
    return null;
  }

  return {
    kind: "section",
    key: subSection.key,
    label: subSection.label,
    description: subSection.description,
    validationKeys,
    validations: getValidationsByKeys(indexes, validationKeys),
    children,
  };
}

function mapLayoutSubSectionKeys(
  subSectionKeys: string[] | null | undefined,
  indexes: PolicyBuilderIndexes,
): PolicySectionNode[] {
  if (!subSectionKeys?.length) {
    return [];
  }

  return subSectionKeys
    .map((key) => mapLayoutSubSectionToNode(key, indexes))
    .filter((node): node is PolicySectionNode => Boolean(node));
}

export function mapLayoutSectionToNode(
  section: IPolicyLayoutSection,
  indexes: PolicyBuilderIndexes,
): PolicySectionNode {
  const validationKeys = section.validation_keys ?? [];

  return {
    kind: "section",
    key: section.key,
    label: section.label,
    description: section.description,
    validationKeys,
    validations: getValidationsByKeys(indexes, validationKeys),
    children: [
      ...mapFieldKeysToNodes(section.field_keys, indexes, ""),
      ...mapLayoutSubSectionKeys(section.sub_section_keys, indexes),
    ],
  };
}

export function mapBuilderConfigToFormNodes(
  indexes: PolicyBuilderIndexes,
): PolicyFormNode[] {
  const nodes: PolicyFormNode[] = [];

  indexes.config.layout_sections.forEach((section) => {
    if (isPolicyRulesLayoutSection(section.key)) {
      nodes.push(...mapRuleCategoryNodes(indexes));
      return;
    }

    nodes.push(mapLayoutSectionToNode(section, indexes));
  });

  return nodes;
}
